// ============================================================
// useClientNotesAutoSave.ts
// Phase 2e. Persists edits to the Client.notes field to the
// backend via PATCH /api/clients/:id. Mirrors useClientAutoSave's
// singleton pattern (one current client at a time, one debounce
// timer, one in-flight slot, one retry timer) but is NOT a
// React hook — it's a set of module-scope helper functions.
//
// Why not a hook: the ClientSearchView detail-mode textarea is
// the only consumer. Its onChange calls scheduleClientNotesSave
// directly, and a separate cancel/flush is wired into the
// nav-away handlers. No store subscription, no useEffect, no
// React lifecycle to coordinate — closer in shape to the
// viewingPaneToggles auto-save in useCasevoStore than to
// useClientAutoSave.
//
// Public API:
//   scheduleClientNotesSave(clientId, notes) — call from the
//       textarea's onChange. Schedules a 1s-debounced save.
//       Skips when the (clientId, notes) pair is unchanged from
//       the last successful save.
//   flushClientNotesAutoSave() — fires any pending edit
//       immediately and awaits the in-flight save plus any
//       follow-up triggered by a keystroke that landed during
//       the request. Called from every nav-away handler.
//   cancelClientNotesAutoSave() — drops the pending save on
//       the floor without firing it. Called when
//       ClientSearchView switches selectedClient — we want to
//       discard the in-flight schedule for client A before
//       client B's notes load into the textarea.
//   retryClientNotesAutoSave() — manual retry for the most
//       recent failure. Kept for parity with useClientAutoSave.
//
// Errors mirror into the existing useCasevoStore.saveError slot
// so any global save-error surface (currently SaveIndicator on
// case detail) sees notes failures the same way it sees case
// and client-record failures. This deliberately shares one slot.
// ============================================================
import { useCasevoStore } from '../store/useCasevoStore'
import { getClientBaseUpdatedAt, setClientBaseUpdatedAt } from './saveBaseVersion'
import { getClientByIdApi } from '../api/casevoApi'
import { MAX_409_REAPPLY_ATTEMPTS } from './reconcile409'

// Module-scope state. Same singleton pattern as useClientAutoSave —
// guaranteed safe because the ClientSearchView textarea is the
// only editing surface and only one client's notes are being
// edited at a time. flush/cancel/retry being plain functions
// means nav handlers don't need a React ref.
//
// pendingTimerId  — active 1s debounce timer (null when nothing pending)
// pendingPayload  — what we'd save when the timer fires (latest snapshot)
// inFlightPromise — currently-running save, if any (null when idle)
// lastSavedKey    — last successfully-sent `${clientId}:${notes}` key.
//                   Compare against on every schedule to skip noop saves.
// lastError       — most recent save failure, mirrored to the store
//                   so SaveIndicator subscribers can render it.
// retryTimerId    — pending one-shot 5s retry timer (null when none).
//                   Cancelled when a fresher keystroke arrives (the
//                   normal debounce handles it) and when a save
//                   succeeds.
type PendingPayload = {
  clientId: string
  notes: string
}

let pendingTimerId: ReturnType<typeof setTimeout> | null = null
let pendingPayload: PendingPayload | null = null
let inFlightPromise: Promise<void> | null = null
let lastSavedKey: string | null = null
let lastError: Error | null = null
let retryTimerId: ReturnType<typeof setTimeout> | null = null

const DEBOUNCE_MS = 1000
const RETRY_DELAY_MS = 5000

// Mirror lastError into the store so SaveIndicator subscribers re-render.
// Outside React, so use the imperative getState() escape hatch.
const publishError = (err: Error | null): void => {
  lastError = err
  useCasevoStore.getState().setSaveError(err)
}

// Compose the skip-if-unchanged key. Cheap to compute and to compare,
// no JSON.stringify needed (notes is a single string).
const buildKey = (clientId: string, notes: string): string => `${clientId}:${notes}`

const runSave = (
  payload: PendingPayload,
  suppressRetry: boolean = false,
  // Column-aware 409 recovery attempt counter (0 = first try).
  attempt: number = 0,
): Promise<void> => {
  // Placeholder so the IIFE's `finally` can refer to `promise` — by the
  // time `finally` actually runs, the reassignment below has happened.
  let promise: Promise<void> = Promise.resolve()
  promise = (async () => {
    try {
      // Go through the store's updateClient action rather than calling
      // updateClientApi directly so the firm-wide `clients[]` array
      // stays in sync with the server's response (including the
      // backend-stamped lastNotesEditedAt + lastNotesEditedById).
      // Participate in the SHARED client concurrency baseline (same map
      // useClientAutoSave uses). Sending baseUpdatedAt + advancing it to
      // the server's new updatedAt keeps the two client autosaves (fields
      // + notes) in lockstep — otherwise a notes save silently bumps the
      // row's updatedAt, leaving the fields hook's base stale so its NEXT
      // save 409s. Opt-in: omit when no base was captured.
      const base = getClientBaseUpdatedAt(payload.clientId)
      const body = base !== null
        ? { notes: payload.notes, baseUpdatedAt: base }
        : { notes: payload.notes }
      const updated = await useCasevoStore.getState().updateClient(payload.clientId, body)
      setClientBaseUpdatedAt(payload.clientId, updated.updatedAt, 'notes-success')
      lastSavedKey = buildKey(payload.clientId, payload.notes)
      // Only clear pendingPayload if it's still the same one we just
      // saved; a newer keystroke during the request may have replaced
      // it already.
      if (
        pendingPayload
        && pendingPayload.clientId === payload.clientId
        && pendingPayload.notes === payload.notes
      ) {
        pendingPayload = null
      }
      // Success — clear any prior error and cancel any pending retry.
      if (lastError !== null) publishError(null)
      if (retryTimerId !== null) {
        clearTimeout(retryTimerId)
        retryTimerId = null
      }
    } catch (err) {
      // 409 = optimistic-concurrency conflict (the client row was written
      // since our base — typically the sibling fields autosave). Mirror
      // useClientAutoSave: advance the shared baseline from the 409 body,
      // do NOT retry (retrying the same stale write just 409s again → the
      // repeated-409 loop this fix removes), drop the stale pending
      // payload, and surface the non-destructive conflict notice.
      const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
      if (resp?.status === 409) {
        if (retryTimerId !== null) { clearTimeout(retryTimerId); retryTimerId = null }

        // ★ COLLAB FIELD — NEVER toast. Client notes is a Yjs-collaborative
        // field: Yjs owns the content merge, and this HTML snapshot is only a
        // PASSIVE MIRROR of the live doc to the DB. A 409 here just means
        // client.updatedAt moved since the base we sent — a sibling fields save,
        // the live-freshness base-advance, or (the co-editing case) ANOTHER
        // editor's own notes snapshot that Yjs has already merged. In every case
        // the authoritative content is the current Y.Doc and our next snapshot
        // carries the merged HTML, so we ALWAYS resolve silently: advance the
        // base to the server's current updatedAt and re-send the latest. We
        // never surface a conflict warning during co-editing.
        let serverUpdatedAt: string | undefined = resp.data?.currentUpdatedAt
        try {
          const server = await getClientByIdApi(payload.clientId)
          serverUpdatedAt = server.updatedAt
        } catch {
          // Re-fetch failed — fall back to the 409 body's currentUpdatedAt.
        }
        if (serverUpdatedAt) setClientBaseUpdatedAt(payload.clientId, serverUpdatedAt, 'notes-409-reapply')
        if (attempt < MAX_409_REAPPLY_ATTEMPTS) {
          await runSave(payload, true, attempt + 1)
          return
        }
        // Cap reached (updatedAt kept moving under us) — STILL never toast. The
        // text is safe in Yjs; clear any error state and schedule one delayed
        // retry so the HTML mirror eventually settles to Saved.
        if (lastError !== null) publishError(null)
        if (retryTimerId !== null) clearTimeout(retryTimerId)
        retryTimerId = setTimeout(() => {
          retryTimerId = null
          if (pendingPayload) runSave(pendingPayload, true, 0)
        }, RETRY_DELAY_MS)
        return
      }
      const error = err instanceof Error ? err : new Error('Client notes auto-save failed')
      publishError(error)
      // eslint-disable-next-line no-console
      console.error('[ClientNotesAutoSave] Failed to save notes:', err)
      // Schedule one retry — unless we're already in a retry, or the
      // caller (flush) explicitly suppressed it.
      if (!suppressRetry) {
        if (retryTimerId !== null) clearTimeout(retryTimerId)
        retryTimerId = setTimeout(() => {
          retryTimerId = null
          // Use the LATEST pendingPayload — if the user typed since,
          // we save the fresher data instead of the original. If
          // pendingPayload is null, an in-between flush already saved
          // and we have nothing to retry.
          if (pendingPayload) runSave(pendingPayload, true)
        }, RETRY_DELAY_MS)
      }
    } finally {
      // Only clear the in-flight slot if WE were the in-flight promise;
      // a concurrent flush may have started another save by now.
      if (inFlightPromise === promise) inFlightPromise = null
    }
  })()
  inFlightPromise = promise
  return promise
}

// ── Stage 1d: apply a REMOTE client-notes edit without a save-loop ────
// Client notes is a rich-text (HTML string) field saved through updateClient
// on the SHARED client base (same map useClientAutoSave uses). This module has
// no store subscription, so writing the store directly triggers no notes save.
// We: (1) mirror the remote value into the store's selectedClient.notes so the
// row stays consistent (and so re-opening the Notes section re-seeds the new
// value, not a stale one), (2) advance the shared client base to the server
// updatedAt the sender's save produced — keeping BOTH client autosaves (fields
// + notes) in lockstep so neither 409s next — and (3) mark this value as
// already-saved (lastSavedKey) so a stray schedule can't resubmit it.
//
// IMPORTANT: this updates the STORE + base only. The visible editor is
// controlled by ClientSearchView's LOCAL notesValue state (seeded on client
// switch, not bound to the store), so the caller (ClientSearchView's receive
// subscription) must ALSO call setNotesValue(notes) to update the on-screen
// TipTap editor — see the note there. No-ops when the edit targets a client
// that isn't the one loaded.
export const applyRemoteClientNotesEdit = (
  clientId: string,
  notes: string,
  serverUpdatedAt: string,
): void => {
  const store = useCasevoStore.getState()
  if (store.selectedClient && store.selectedClient.id === clientId) {
    // buildClientPayload (useClientAutoSave) does NOT include notes, so this
    // store write stages no client-field PATCH — no loop.
    store.updateSelectedClient({ notes })
  }
  setClientBaseUpdatedAt(clientId, serverUpdatedAt, 'notes-remote-apply')
  lastSavedKey = buildKey(clientId, notes)
}

// Public: schedule a debounced save. Call from the textarea's onChange
// after updating the local state. Skip-if-unchanged so repeated
// keystrokes that bring the value back to its last-saved state don't
// trigger a redundant network roundtrip.
export const scheduleClientNotesSave = (clientId: string, notes: string): void => {
  if (buildKey(clientId, notes) === lastSavedKey) return

  pendingPayload = { clientId, notes }

  // A new keystroke supersedes any pending retry — the regular
  // debounce will pick up this fresher data shortly anyway.
  if (retryTimerId !== null) {
    clearTimeout(retryTimerId)
    retryTimerId = null
  }

  if (pendingTimerId !== null) clearTimeout(pendingTimerId)
  pendingTimerId = setTimeout(() => {
    pendingTimerId = null
    if (pendingPayload) runSave(pendingPayload)
  }, DEBOUNCE_MS)
}

// Public: force any pending notes edit to persist now. Call before
// navigating away (sidebar handlers, case-detail nav, etc.) so the
// user doesn't lose a sub-second debounce window. Safe to call when
// nothing's pending — resolves immediately.
export const flushClientNotesAutoSave = async (): Promise<void> => {
  if (pendingTimerId !== null) {
    clearTimeout(pendingTimerId)
    pendingTimerId = null
  }
  if (inFlightPromise) {
    try { await inFlightPromise } catch { /* logged in runSave */ }
  }
  if (pendingPayload) {
    try { await runSave(pendingPayload, true) } catch { /* logged in runSave */ }
  }
}

// Public: drop the pending save on the floor without firing it.
// Used when ClientSearchView switches selectedClient — we want to
// discard the in-flight schedule for client A before client B's
// notes load into the textarea. Different from flush: flush WAITS
// for the save; cancel just abandons the buffered edit.
//
// NOTE: this does NOT cancel an in-flight save. If a save for
// client A is already mid-request when the user picks client B,
// it'll complete normally — the request body is already on the
// wire, cancelling at this point would risk an inconsistent
// state. Only the debounce timer + buffered payload are dropped.
export const cancelClientNotesAutoSave = (): void => {
  if (pendingTimerId !== null) {
    clearTimeout(pendingTimerId)
    pendingTimerId = null
  }
  if (retryTimerId !== null) {
    clearTimeout(retryTimerId)
    retryTimerId = null
  }
  pendingPayload = null
}

// Public: manual retry of the last failed notes save. Kept for
// parity with retryClientAutoSave so a future SaveIndicator
// extension can distinguish notes retries from case/client retries.
export const retryClientNotesAutoSave = async (): Promise<void> => {
  if (lastError === null) return
  if (retryTimerId !== null) {
    clearTimeout(retryTimerId)
    retryTimerId = null
  }
  if (inFlightPromise) {
    try { await inFlightPromise } catch { /* logged in runSave */ }
  }
  if (lastError === null || pendingPayload === null) return
  await runSave(pendingPayload, true)
}

// beforeunload guard — mirrors useClientAutoSave's pattern but
// registered at module load (not inside a useEffect) because this
// module has no React hook. Safe: the module is imported by the
// sidebar's flush handlers, so it loads on app start. The handler
// itself is a no-op when nothing is pending/in-flight, so it adds
// nothing to the unload path in the common case.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (pendingPayload === null && inFlightPromise === null) return
    event.preventDefault()
    event.returnValue = ''
  })
}
